/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';

import type { DocNode } from '@atlaskit/adf-schema';
import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { AIChatIcon, ToolbarButton, ToolbarTooltip } from '@atlaskit/editor-toolbar';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Form, { Field, type FormApi } from '@atlaskit/form';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import SendIcon from '@atlaskit/icon/core/send';
import { RovoIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { ChatPill } from '@atlaskit/rovo-agent-components';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import Textfield from '@atlaskit/textfield';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation';

import { appendADFContent } from '../../common/utils/adf';
import {
	useRovoChatHighlightActions,
	useRovoChatHighlightActionsActions,
} from '../../controllers/highlight-actions';
import { useHighlightActionsEditorPlaceholder } from '../../controllers/highlight-actions-editor-placeholder';

import messages from './messages';

const styles = cssMap({
	popupContainer: {
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
	},
	// The form component in jira and the field component in confluence both have a default marginBlockStart which we need to override
	formWrapper: {
		marginBlockStart: token('space.negative.100'),
	},
	sendIconWrapper: {
		paddingInlineEnd: token('space.150'),
	},
	// TODO: https://product-fabric.atlassian.net/browse/ROVOFE-699 - Migrate other buttons in Confluence highlight actions menu to use new button instead of old to align with our button
	// The other items that live in the highlight actions menu in Confluence are using the old button component
	// so we need to offset the icon button to the left to align with the other items.
	// This is a temporary solution until we migrate the other buttons to the new button component.
	rovoChatMoreOptionsButtonContainer: {
		marginInlineStart: token('space.negative.100'),
	},
	/**
	 * Re-centers content in the button that opens chat.
	 */
	rovoChatOpenChatIcon: {
		paddingInlineStart: token('space.025'),
	},
});

interface FormValues {
	prompt: string;
}

interface RovoChatActionsProps {
	selectedContent: DocNode;
	product: string;
	onClickChat: () => void;
	onClickPromptOverride?: (prompt: DocNode) => void;
	onSubmitCustomPromptOverride?: (prompt: DocNode) => void;
}

export const RovoChatHighlightActions = ({
	selectedContent,
	product,
	onClickChat,
	onClickPromptOverride,
	onSubmitCustomPromptOverride,
}: RovoChatActionsProps) => {
	di(FeatureGates, useRovoChatHighlightActions);
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const { setIsMoreOptionsPopupOpen } = useRovoChatHighlightActionsActions();
	const [{ isMoreOptionsPopupOpen }] = useRovoChatHighlightActions();
	const { insertHighlightActionsEditorPlaceholder } = useHighlightActionsEditorPlaceholder();

	useEffect(() => {
		sendAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'contextualMenuItem',
			actionSubjectId: 'rovoChatHighlightActions',
			attributes: {
				product,
			},
		});
	}, [sendAnalyticsEvent, product]);

	const onClose = useCallback(() => {
		setIsMoreOptionsPopupOpen(false);
	}, [setIsMoreOptionsPopupOpen]);

	const handleClickChat = useCallback(async () => {
		onClickChat();
		insertHighlightActionsEditorPlaceholder(selectedContent);
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'highlightActionsMenuRovoChatButton',
			attributes: {
				product,
			},
		});
	}, [
		onClickChat,
		insertHighlightActionsEditorPlaceholder,
		selectedContent,
		sendAnalyticsEvent,
		product,
	]);

	const handlePromptSubmit = useCallback(
		(values: FormValues, form: FormApi<FormValues>) => {
			const formattedPrompt = appendADFContent(selectedContent, values.prompt);
			if (onSubmitCustomPromptOverride) {
				onSubmitCustomPromptOverride(formattedPrompt);
				form.reset();
				sendAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'highlightActionsMenuSubmitPromptButton',
					attributes: {
						product,
					},
				});
				return;
			}
			publish({
				type: 'chat-new',
				source: `${product}-context-menu`,
				data: {
					name: values.prompt.substring(0, 30),
					dialogues: [],
					prompt: formattedPrompt,
				},
			});
			form.reset();
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'highlightActionsMenuSubmitPromptButton',
				attributes: {
					product,
				},
			});
		},
		[publish, sendAnalyticsEvent, product, onSubmitCustomPromptOverride, selectedContent],
	);

	const rovoChatPromptOptions = [
		{
			id: 'context-menu-rovo-chat-summarize-option',
			actionType: 'summarize',
			prompt: formatMessage(messages.rovoChatSummarizePrompt),
			optionText: formatMessage(messages.rovoChatSummarizeOptionText),
		},
		{
			id: 'context-menu-rovo-chat-explain-option',
			actionType: 'explain',
			prompt: formatMessage(messages.rovoChatExplainPrompt),
			optionText: formatMessage(messages.rovoChatExplainOptionText),
		},
		{
			id: 'context-menu-rovo-chat-find-resources-option',
			actionType: 'find-resources',
			prompt: formatMessage(messages.rovoChatFindResourcesPrompt),
			optionText: formatMessage(messages.rovoChatFindResourcesOptionText),
		},
		{
			id: 'context-menu-rovo-chat-give-feedback-option',
			actionType: 'give-feedback',
			prompt: formatMessage(messages.rovoChatGiveFeedbackPrompt),
			optionText: formatMessage(messages.rovoChatGiveFeedbackOptionText),
		},
		{
			id: 'context-menu-rovo-chat-give-examples-option',
			actionType: 'give-examples',
			prompt: formatMessage(messages.rovoChatGiveExamplesPrompt),
			optionText: formatMessage(messages.rovoChatGiveExamplesOptionText),
		},
	];

	const renderPopupContent = () => {
		return (
			<Stack testId="context-menu-rovo-chat-popup" xcss={styles.popupContainer} space="space.150">
				{rovoChatPromptOptions.map((option) => {
					return (
						<ChatPill
							key={option.id}
							testId={option.id}
							onClick={() => {
								const formattedPrompt = appendADFContent(selectedContent, option.prompt);
								if (onClickPromptOverride) {
									onClickPromptOverride(formattedPrompt);
									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'highlightActionsMenuConvoStarterButton',
										attributes: {
											actionType: option.actionType,
											product,
										},
									});
									return;
								}
								publish({
									type: 'chat-new',
									source: `${product}-context-menu`,
									data: {
										name: option.prompt.substring(0, 30),
										dialogues: [],
										prompt: formattedPrompt,
									},
								});
								sendAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'highlightActionsMenuConvoStarterButton',
									attributes: {
										actionType: option.actionType,
										product,
									},
								});
							}}
						>
							{option.optionText}
						</ChatPill>
					);
				})}
				{fg('platform-design_system_team-form_conversion') ? (
					<Form onSubmit={handlePromptSubmit}>
						<Field name="prompt">
							{({ fieldProps: { value, ...fieldPropsRest }, meta: { dirty } }) => (
								<Textfield
									// this prevents the highlight actions popup from closing when clicking on the input
									onClick={(e) => e.stopPropagation()}
									{...fieldPropsRest}
									testId="context-menu-rovo-chat-custom-prompt"
									id="context-menu-rovo-chat-custom-prompt"
									autoComplete="off"
									elemAfterInput={
										<Box xcss={styles.sendIconWrapper}>
											<IconButton
												testId="context-menu-rovo-chat-custom-prompt-submit"
												isDisabled={!dirty || !value}
												label={formatMessage(messages.rovoChatCustomPromptSubmitButtonText)}
												type="submit"
												icon={SendIcon}
												spacing="compact"
											/>
										</Box>
									}
									placeholder={formatMessage(messages.rovoChatCustomPromptPlaceholder)}
									aria-label={formatMessage(messages.rovoChatCustomPromptPlaceholder)}
								/>
							)}
						</Field>
					</Form>
				) : (
					<Form onSubmit={handlePromptSubmit}>
						{({ formProps }) => (
							<Box xcss={styles.formWrapper}>
								<form {...formProps}>
									<Field name="prompt">
										{({ fieldProps: { value, ...fieldPropsRest }, meta: { dirty } }) => (
											<Textfield
												// this prevents the highlight actions popup from closing when clicking on the input
												onClick={(e) => e.stopPropagation()}
												{...fieldPropsRest}
												testId="context-menu-rovo-chat-custom-prompt"
												id="context-menu-rovo-chat-custom-prompt"
												autoComplete="off"
												elemAfterInput={
													<Box xcss={styles.sendIconWrapper}>
														<IconButton
															testId="context-menu-rovo-chat-custom-prompt-submit"
															isDisabled={!dirty || !value}
															label={formatMessage(messages.rovoChatCustomPromptSubmitButtonText)}
															type="submit"
															icon={SendIcon}
															spacing="compact"
														/>
													</Box>
												}
												placeholder={formatMessage(messages.rovoChatCustomPromptPlaceholder)}
												aria-label={formatMessage(messages.rovoChatCustomPromptPlaceholder)}
											/>
										)}
									</Field>
								</form>
							</Box>
						)}
					</Form>
				)}
			</Stack>
		);
	};

	const isToolbarAIFCEnabled = editorExperiment('platform_editor_toolbar_aifc', true);

	if (product === 'confluence' && isToolbarAIFCEnabled) {
		return (
			<ToolbarTooltip content={formatMessage(messages.rovoChatOpenChatTooltip)}>
				<ToolbarButton
					iconBefore={<AIChatIcon label="" />}
					onClick={handleClickChat}
					testId="context-menu-rovo-chat-button"
				>
					{formatMessage(messages.rovoChatAskRovoButtonText)}
				</ToolbarButton>
			</ToolbarTooltip>
		);
	}

	return (
		<Inline>
			<Tooltip content={formatMessage(messages.rovoChatOpenChatTooltip)}>
				<Button
					appearance="subtle"
					iconBefore={() => (
						<Inline xcss={styles.rovoChatOpenChatIcon}>
							<RovoIcon size="xsmall" />
						</Inline>
					)}
					onClick={handleClickChat}
					testId="context-menu-rovo-chat-button"
				>
					<Text weight="medium">{formatMessage(messages.rovoChatAskRovoButtonText)}</Text>
				</Button>
			</Tooltip>
			<Popup
				placement="bottom-start"
				isOpen={isMoreOptionsPopupOpen}
				onClose={onClose}
				content={renderPopupContent}
				zIndex={600}
				trigger={(triggerProps) => (
					<Box xcss={styles.rovoChatMoreOptionsButtonContainer}>
						<IconButton
							{...triggerProps}
							appearance="subtle"
							id="context-menu-rovo-chat-more-options"
							testId="context-menu-rovo-chat-more-options"
							isTooltipDisabled={false}
							icon={(props) => (
								<ChevronDownIcon
									{...props}
									label={formatMessage(messages.rovoChatMoreOptionsTooltip)}
									size="small"
								/>
							)}
							label={formatMessage(messages.rovoChatMoreOptionsTooltip)}
							onClick={() => {
								setIsMoreOptionsPopupOpen(!isMoreOptionsPopupOpen);
								sendAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'highlightActionsMenuRovoChatMoreOptionsButton',
									attributes: {
										product,
									},
								});
							}}
						/>
					</Box>
				)}
			/>
		</Inline>
	);
};
